import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatINR, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Send, Printer } from "lucide-react";
import { toast } from "sonner";

export default function PartyLedger() {
  const { partyId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [form, setForm] = useState({ amount_paid: "", payment_mode: "Cash", reference_no: "", notes: "", payment_date: new Date().toISOString().slice(0, 10) });

  const load = async () => {
    try {
      const { data } = await api.get(`/ledger/party/${partyId}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [partyId]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/ledger/party/payments", { party_id: partyId, ...form, amount_paid: parseFloat(form.amount_paid) });
      toast.success("Payment recorded");
      setPayOpen(false);
      setForm({ ...form, amount_paid: "", reference_no: "", notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const share = async () => {
    try {
      const { data: r } = await api.post(`/ledger/share/whatsapp/party/${partyId}`);
      toast.success(`Statement sent via ${r.sent_via}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;

  const p = data.party;
  const t = data.totals;

  return (
    <div className="space-y-5" data-testid="party-ledger-page">
      <Button variant="ghost" onClick={() => navigate("/parties")} className="rounded-none" data-testid="back-to-parties"><ArrowLeft className="w-4 h-4 mr-2" />Back to Parties</Button>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Kpi label="Party" value={p.party_name || p.trade_name} />
        <Kpi label="Total Purchases" value={formatINR(t.total_purchases)} />
        <Kpi label="Total Paid" value={formatINR(t.total_paid)} />
        <Kpi label="Outstanding" value={formatINR(t.outstanding)} accent />
      </div>

      <div className="flex gap-2">
        <Button onClick={() => setPayOpen(true)} className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="record-party-pay-btn"><Plus className="w-4 h-4 mr-1" />Record Payment</Button>
        <Button variant="outline" onClick={share} className="rounded-none" data-testid="share-party-statement"><Send className="w-4 h-4 mr-1" />Share via WhatsApp</Button>
        <Button variant="outline" onClick={() => window.print()} className="rounded-none" data-testid="print-party-statement"><Printer className="w-4 h-4 mr-1" />Print</Button>
      </div>

      <div className="border border-slate-200 bg-white rounded-sm overflow-x-auto">
        <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 text-xs uppercase tracking-[0.2em] text-slate-500">Ledger Statement (chronological)</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            <th className="px-4 py-3 text-left font-semibold">Date</th>
            <th className="px-4 py-3 text-left font-semibold">Ref</th>
            <th className="px-4 py-3 text-left font-semibold">Description</th>
            <th className="px-4 py-3 text-right font-semibold">Debit (Paid)</th>
            <th className="px-4 py-3 text-right font-semibold">Credit (Purchase)</th>
            <th className="px-4 py-3 text-right font-semibold">Balance</th>
          </tr></thead>
          <tbody>
            {data.entries.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No entries.</td></tr>}
            {data.entries.map((e, i) => (
              <tr key={i} className={`border-b border-slate-100 ${e.type === "payment" ? "bg-green-50/40" : ""}`} data-testid={`pl-entry-${i}`}>
                <td className="px-4 py-3 text-xs">{e.date}</td>
                <td className="px-4 py-3 font-mono text-xs">{e.ref}</td>
                <td className="px-4 py-3 text-slate-700">{e.description}</td>
                <td className="px-4 py-3 text-right tabular-nums text-green-700">{e.debit > 0 ? formatINR(e.debit) : ""}</td>
                <td className="px-4 py-3 text-right tabular-nums text-orange-700">{e.credit > 0 ? formatINR(e.credit) : ""}</td>
                <td className="px-4 py-3 text-right tabular-nums font-bold">{formatINR(e.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle>Record Payment to {p.party_name || p.trade_name}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="text-xs text-slate-500">Outstanding: <span className="font-bold text-slate-900">{formatINR(t.outstanding)}</span></div>
            <div><Label>Amount</Label><Input type="number" step="0.01" min="0.01" required value={form.amount_paid} onChange={(e) => setForm({ ...form, amount_paid: e.target.value })} className="rounded-none mt-1" data-testid="pl-amount" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Mode</Label>
                <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="pl-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                    <SelectItem value="Card">Card</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Date</Label><Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="rounded-none mt-1" data-testid="pl-date" /></div>
            </div>
            <div><Label>Reference (cheque/UTR/UPI ref)</Label><Input value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} className="rounded-none mt-1" data-testid="pl-ref" /></div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none mt-1" data-testid="pl-notes" /></div>
            <DialogFooter><Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="pl-submit">Record</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={`p-4 border ${accent ? "border-orange-300 bg-orange-50" : "border-slate-200 bg-white"} rounded-sm`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="text-xl font-bold mt-1 tabular-nums break-all">{value}</div>
    </div>
  );
}
